import {ApolloQueryResult} from '@apollo/client';
import {noop} from '@benchling/js-utils';
import {objectFromEntries} from '@benchling/js-utils';
import React, {useState} from 'react';

import {Owner} from 'auth/PermissionConstants';
import {ClientTagSchemaConstraint, RegistryLabelingStrategy, SavingState} from 'constants/RegistryConstants';
import {QueryResultType, useBenchlingQuery} from 'graphql-client/BenchlingQuery';
import ExtractByTypename from 'graphql-client/ExtractByTypename';
import {assertIsAccessible} from 'graphql-client/InaccessibleInfo';
import {NameTemplatePart} from 'name-templates/NameTemplateConstants';
import notifications from 'notifications';
import * as LabelingStrategiesUtil from 'registry/labeling-strategies/LabelingStrategiesUtil';
import {InterfaceFieldChangePropagationWarningQuery} from 'schemas/fields/__generated/InterfaceFieldChangePropagationWarningQuery';
import {
  INTERFACE_FIELD_CHANGE_PROPAGATION_WARNING_QUERY,
  InterfaceFieldChangePropagationWarning,
} from 'schemas/fields/FieldChangeWarningModal';
import {InterfaceField} from 'schemas/fields/SchemaFieldConstants';
import {SchemaInterfaceQuery} from 'schemas/schema-interface/__generated/SchemaInterfaceQuery';
import ImplementingSchemasTable from 'schemas/schema-interface/ImplementingSchemasTable';
import {schemaFieldsetName} from 'schemas/schema-interface/util/schemaFieldsetName';
import getTestProps from 'test/lib/cypress/getTestProps';
import {Tab, TabContainer} from 'ui-library/TabContainer';
import {capwords} from 'util/NameUtil';
import {withLoadingScreen} from 'util/Promises';

import {SchemaPermissionsGraphQL} from '../../../graphql-fragments/schemas/__generated/SchemaPermissionsGraphQL';
import {OptionalBaseBioentityType} from './SchemaInterfaceBaseBioentityTypeDropdown';
import {SchemaInterface} from './SchemaInterfaceConstants';
import {
  addSchemaInterfaceField,
  updateSchemaInterface,
  removeSchemaInterfaceField,
  UpdateSchemaInterfaceRequest,
  updateSchemaInterfaceField,
  convertToSchemaConstraintUpdatePayload,
  setSchemaInterfaceNameTemplate,
  convertToSchemaInterfaceFieldForRequest,
} from './SchemaInterfaceDAO';
import SchemaInterfaceEditFieldTable from './SchemaInterfaceEditFieldTable';
import SchemaInterfaceMetadataSettings from './SchemaInterfaceMetadataSettings';
import SchemaInterfacePermissionSettings from './SchemaInterfacePermissionSettings';
import {
  ClientInterfaceField,
  convertGQLSchemaInterfaceFieldToClientInterfaceField,
} from './SchemaInterfaceUtils';
import {coerceConstraintShape} from './SchemaInterfaceUtils';
import {
  getInterfaceConstraintErrorsForInterface,
  InterfaceConstraintErrorDisplayDataForInterface,
} from './validation/SchemaInterfaceValidationUtils';

type SchemaInterfaceNode = ExtractByTypename<
  InterfaceFieldChangePropagationWarningQuery['schemaInterface'],
  'SchemaInterfaceNode'
>;

export enum Tabs {
  SCHEMA_INTERFACE_EDITOR = 'SCHEMA_INTERFACE_EDITOR',
  SCHEMA_INTERFACE_ACCESS_POLICIES = 'SCHEMA_INTERFACE_ACCESS_POLICIES',
  SCHEMA_INTERFACE_IMPLEMENTING_SCHEMAS = 'SCHEMA_INTERFACE_IMPLEMENTING_SCHEMAS',
}

export interface ExistingSchemaInterfaceEditorProps {
  interfaceId?: string;
  interface: SchemaInterface;
  ownerChoices: Array<Owner> | null;
  schemaPermissions: SchemaPermissionsGraphQL | null;
  refetch: () => Promise<ApolloQueryResult<SchemaInterfaceQuery>>;
}

export default function ExistingSchemaInterfaceEditor(
  props: ExistingSchemaInterfaceEditorProps
): JSX.Element {
  const baseBioentityType: OptionalBaseBioentityType = props.interface
    .baseBioentityType as OptionalBaseBioentityType;

  const fields = props.interface.fields.map((field) =>
    convertGQLSchemaInterfaceFieldToClientInterfaceField(field)
  );

  const labelingStrategies = props.interface.labelingStrategies;
  // This should always be a list of 1
  const graphQLNameTemplate =
    props.interface.nameTemplates.length > 0 ? props.interface.nameTemplates[0] : null;
  const graphQLNameTemplateParts = graphQLNameTemplate != null ? graphQLNameTemplate.nameTemplateParts : [];

  const nameTemplateParts: Array<NameTemplatePart> = graphQLNameTemplateParts.map(
    (graphQLNameTemplatePart) =>
      objectFromEntries(
        Object.entries(graphQLNameTemplatePart).filter(([k, v]) => v != null && k !== '__typename')
      ) as NameTemplatePart
  );

  const prefix = props.interface.prefix;

  const shouldOrderPartsBySequence: boolean =
    graphQLNameTemplate != null ? graphQLNameTemplate.shouldOrderPartsBySequence : false;

  const [activeTab, setActiveTab] = useState<Tabs>(Tabs.SCHEMA_INTERFACE_EDITOR);
  const [fieldSavingState, setFieldSavingState] = useState<SavingState>(SavingState.COMPLETED);
  const [interfaceConstraintErrors, setInterfaceConstraintErrors] = useState<
    Array<InterfaceConstraintErrorDisplayDataForInterface>
  >([]);
  const [enforceLabelingStrategies, setEnforceLabelingStrategies] = useState<boolean>(
    labelingStrategies.length > 0
  );

  const handleNameChange = (schemaInterfaceName: string): void => {
    updateInterface({
      name: schemaInterfaceName,
    });
  };

  const handleSqlIdentifierChange = (schemaInterfaceSqlIdentifier: string): void => {
    updateInterface({
      sqlIdentifier: schemaInterfaceSqlIdentifier,
    });
  };

  const handleBaseBioentityTypeChange = (
    schemaInterfaceBaseBioentityType: OptionalBaseBioentityType
  ): void => {
    updateInterface({
      baseBioentityType: schemaInterfaceBaseBioentityType,
    });
  };

  const handleConstraintChange = (schemaInterfaceConstraint: ClientTagSchemaConstraint | null): void => {
    const constraint = schemaInterfaceConstraint
      ? convertToSchemaConstraintUpdatePayload(schemaInterfaceConstraint)
      : null;
    updateInterface({constraint});
  };

  const handlePrefixChange = (schemaInterfacePrefix: string): void => {
    updateInterface({prefix: schemaInterfacePrefix});
  };

  const handleLabelingStrategiesChange = (labelingStrategies: Array<RegistryLabelingStrategy>): void => {
    updateInterface({
      labelingStrategies,
    });
  };

  const handleEnforceLabelingStrategiesChange = (enableNamingStrategies: boolean): void => {
    setEnforceLabelingStrategies(enableNamingStrategies);
    if (!enableNamingStrategies) {
      handleLabelingStrategiesChange([]);
    } else {
      const initialLabelingStrategies = LabelingStrategiesUtil.getInitialLabelingStrategies();
      handleLabelingStrategiesChange(initialLabelingStrategies);
      updateInterface({
        labelingStrategies: initialLabelingStrategies,
      });
    }
  };

  const handleNameTemplateChange = async (
    nameTemplateParts: Array<NameTemplatePart>,
    shouldOrderPartsBySequence: boolean
  ): Promise<void> => {
    await setSchemaInterfaceNameTemplate(
      props.interface.id,
      nameTemplateParts,
      shouldOrderPartsBySequence
    ).then(async () => {
      await props.refetch();
      notifications.success('Set name template successfully.');
    });
  };

  const updateInterface = (data: UpdateSchemaInterfaceRequest): void => {
    withLoadingScreen(
      (async () => {
        await updateSchemaInterface(props.interface.id, data)
          .then(async () => {
            await props.refetch();
            notifications.success(`Updated ${schemaFieldsetName()} successfully.`);
            setInterfaceConstraintErrors([]);
          })
          .catch((e) => {
            if (e.responseJSON?.interfaceConstraintError) {
              const interfaceConstraintErrors = getInterfaceConstraintErrorsForInterface(e.responseJSON);
              if (interfaceConstraintErrors) {
                setInterfaceConstraintErrors(interfaceConstraintErrors);
              }
            }
            notifications.notifyError(e);
          });
      })(),
      'Updating...'
    );
  };

  const addInterfaceField = (field: ClientInterfaceField): void => {
    withLoadingScreen(
      (async () => {
        setFieldSavingState(SavingState.PENDING);
        await addSchemaInterfaceField({
          interfaceId: props.interface.id,
          field: convertToSchemaInterfaceFieldForRequest(field),
        })
          .then(async () => {
            await props.refetch();
            notifications.success(`Updated ${schemaFieldsetName()} successfully.`);
            setInterfaceConstraintErrors([]);
            setFieldSavingState(SavingState.SUCCESS);
          })
          .catch((e) => {
            if (e.responseJSON?.interfaceConstraintError) {
              const interfaceConstraintErrors = getInterfaceConstraintErrorsForInterface(e.responseJSON);
              if (interfaceConstraintErrors) {
                setInterfaceConstraintErrors(interfaceConstraintErrors);
              }
            }
            setFieldSavingState(SavingState.FAILURE);
            notifications.notifyError(e);
          });
      })(),
      'Updating...'
    );
  };

  const updateInterfaceField = async (field: InterfaceField): Promise<void> => {
    await withLoadingScreen(
      (async () => {
        try {
          setFieldSavingState(SavingState.PENDING);
          await updateSchemaInterfaceField({
            interfaceId: props.interface.id,
            fieldId: field.apiId,
            field: convertToSchemaInterfaceFieldForRequest(field),
          });
          await props.refetch();
          notifications.success(`Updated ${schemaFieldsetName()} successfully.`);
          setFieldSavingState(SavingState.SUCCESS);
          setInterfaceConstraintErrors([]);
          // eslint-disable-next-line benchlint/always-throw-from-catch
        } catch (error) {
          setFieldSavingState(SavingState.FAILURE);
          notifications.notifyError(error);
        }
      })(),
      'Updating...'
    );
  };

  const removeInterfaceField = (fieldId: string): void => {
    withLoadingScreen(
      (async () => {
        setFieldSavingState(SavingState.PENDING);
        try {
          await removeSchemaInterfaceField({interfaceId: props.interface.id, fieldId});
          await props.refetch();
          notifications.success(`Updated ${schemaFieldsetName()} successfully.`);
          setFieldSavingState(SavingState.SUCCESS);
        } catch (e) {
          setFieldSavingState(SavingState.FAILURE);
          throw e;
        }
      })(),
      'Updating...'
    );
  };

  const interfaceFieldChangePropagationWarningQueryResult =
    useBenchlingQuery<InterfaceFieldChangePropagationWarningQuery>(
      INTERFACE_FIELD_CHANGE_PROPAGATION_WARNING_QUERY,
      {
        variables: {interfaceId: props.interface.id},
        renderCachedDataWhenRefetching: true,
        fetchPolicy: 'cache-and-network',
        pollInterval: 120_000,
      }
    );

  let shouldShowFieldChangePropagationWarning = true;
  if (interfaceFieldChangePropagationWarningQueryResult.type === QueryResultType.SUCCESS) {
    const schemaInterfaceNode = assertIsAccessible<SchemaInterfaceNode>(
      interfaceFieldChangePropagationWarningQueryResult.data.schemaInterface
    );
    if (schemaInterfaceNode.schemas.length === 0) {
      shouldShowFieldChangePropagationWarning = false;
    }
  }

  return (
    <div
      className="u-flex-grow"
      {...getTestProps({component: 'ExistingSchemaInterfaceEditor', element: 'root'})}
    >
      <TabContainer
        activeTab={activeTab}
        onRequestTabChange={(tab: Tabs) => {
          setActiveTab(tab);
        }}
        className="u-flex-grow"
      >
        <Tab tabKey={Tabs.SCHEMA_INTERFACE_EDITOR} title={`${capwords(schemaFieldsetName())} Settings`}>
          <SchemaInterfaceMetadataSettings
            interfaceId={props.interface.id}
            interface={props.interface}
            ownerId={props.interface.organization.id}
            setOwnerId={noop}
            name={props.interface.name}
            setName={handleNameChange}
            sqlIdentifier={props.interface.sqlIdentifier}
            setSqlIdentifier={handleSqlIdentifierChange}
            constraint={coerceConstraintShape(props.interface.constraint)}
            setConstraint={handleConstraintChange}
            ownerChoices={props.ownerChoices}
            fields={fields}
            fieldTable={
              <div className="u-large-spacing-bottom">
                <SchemaInterfaceEditFieldTable
                  schemaId={props.interface.id}
                  onFieldAdd={addInterfaceField}
                  onFieldRemove={removeInterfaceField}
                  onFieldUpdate={updateInterfaceField}
                  ownerId={props.interface.organization.id}
                  ownerUrlId={props.interface.organization.urlIdentifier}
                  isDisabled={false}
                  fields={fields}
                  savingState={fieldSavingState}
                  submissionWarnings={
                    shouldShowFieldChangePropagationWarning
                      ? [
                          <InterfaceFieldChangePropagationWarning
                            interfaceId={props.interface.id}
                            key="interface-propagation"
                          />,
                        ]
                      : []
                  }
                  baseBioentityType={baseBioentityType}
                />
              </div>
            }
            interfaceConstraintErrors={interfaceConstraintErrors}
            baseBioentityType={baseBioentityType}
            setBaseBioentityType={handleBaseBioentityTypeChange}
            prefix={prefix}
            setPrefix={handlePrefixChange}
            enforceLabelingStrategies={enforceLabelingStrategies}
            setEnforceLabelingStrategies={handleEnforceLabelingStrategiesChange}
            labelingStrategies={labelingStrategies}
            setLabelingStrategies={handleLabelingStrategiesChange}
            nameTemplateParts={nameTemplateParts}
            shouldOrderPartsBySequence={shouldOrderPartsBySequence}
            setNameTemplate={handleNameTemplateChange}
          />
        </Tab>

        <Tab tabKey={Tabs.SCHEMA_INTERFACE_ACCESS_POLICIES} title="Access Policies">
          <SchemaInterfacePermissionSettings schemaInterfaceId={props.interface.id} />
        </Tab>
        <Tab tabKey={Tabs.SCHEMA_INTERFACE_IMPLEMENTING_SCHEMAS} title="Schemas">
          <ImplementingSchemasTable interface={props.interface} />
        </Tab>
      </TabContainer>
    </div>
  );
}
